import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  Handle, Position, applyNodeChanges,
  type Node, type Edge, type NodeChange, type NodeTypes, type NodeProps, type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, Typography, Chip, LinearProgress } from '@mui/material';
import { layoutNodes } from '../lib/dagreLayout';

interface ApItem {
  id: string;
  nr: string;
  parent_id: string | null;
  titel: string | null;
  status: string;
  progress: number;
  is_milestone: boolean;
}

interface ApWbsTreeProps {
  projectName: string;
  items: ApItem[];
  /** Kind unter ein neues Eltern-AP hängen (null = oberste Ebene). */
  onReparent?: (childId: string, newParentId: string | null) => void;
  onSelect?: (id: string) => void;
}

const NODE_W = 184;
const NODE_H = 70;

const STATUS_COLOR: Record<string, string> = {
  'Offen': '#94a3b8',
  'In Arbeit': '#1D8296',
  'Erledigt': '#16a34a',
  'Blockiert': '#dc2626',
};
const STATUS_CHIP: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  'Offen': 'default', 'In Arbeit': 'info', 'Erledigt': 'success', 'Blockiert': 'error',
};

interface WbsNodeData extends Record<string, unknown> {
  nr: string; titel: string | null; status: string; progress: number; milestone: boolean;
}

/** WBS-Knoten: Eltern-Eingang oben (target), Kind-Ausgang unten (source) — die
 *  Höhe/Ebene zeigt die Hierarchie (oben = übergeordnet). */
function WbsNode({ data, selected }: NodeProps) {
  const d = data as WbsNodeData;
  const accent = STATUS_COLOR[d.status] ?? '#94a3b8';
  const progress = Math.max(0, Math.min(100, d.progress ?? 0));
  return (
    <Box
      sx={{
        width: NODE_W, minHeight: NODE_H, boxSizing: 'border-box',
        bgcolor: 'var(--pm-bg-base)', border: '2px solid',
        borderColor: selected ? 'var(--pm-fg-accent)' : accent,
        borderRadius: 1.5, px: 1, py: 0.75, boxShadow: 2,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#1D8296' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--pm-fg-base)' }}>
          {d.nr}{d.milestone ? ' ◆' : ''}
        </Typography>
        <Chip label={d.status} size="small" color={STATUS_CHIP[d.status] ?? 'default'} sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }} />
      </Box>
      <Typography variant="caption" sx={{ display: 'block', color: 'var(--pm-fg-muted)', lineHeight: 1.25, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.titel ?? '—'}
      </Typography>
      <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#1D8296' }} />
    </Box>
  );
}

const nodeTypes: NodeTypes = { wbsNode: WbsNode };

function Canvas({ items, onReparent }: { items: ApItem[]; onReparent?: ApWbsTreeProps['onReparent'] }) {
  const layouted = useMemo(() => {
    const rawNodes: Node[] = items.map((ap) => ({
      id: ap.id,
      type: 'wbsNode',
      position: { x: 0, y: 0 },
      data: { nr: ap.nr, titel: ap.titel, status: ap.status, progress: ap.progress, milestone: ap.is_milestone } satisfies WbsNodeData,
    }));
    // Kanten = Eltern → Kind (parent_id). Verwaiste Eltern werden ignoriert.
    const ids = new Set(items.map((i) => i.id));
    const edges: Edge[] = items
      .filter((ap) => ap.parent_id && ids.has(ap.parent_id))
      .map((ap) => ({ id: `e-${ap.parent_id}-${ap.id}`, source: ap.parent_id!, target: ap.id, type: 'smoothstep' }));
    const positioned = layoutNodes(rawNodes, edges, NODE_W, NODE_H, 'TB');
    return { nodes: positioned, edges };
  }, [items]);

  const [nodes, setNodes] = useState<Node[]>(layouted.nodes);
  // Bei Daten-/Layoutänderung neu anordnen (auto-Layout, keine Positions-Speicherung).
  useEffect(() => { setNodes(layouted.nodes); }, [layouted.nodes]);
  const onNodesChange = useCallback((changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)), []);

  // Strich umhängen: conn.source = neues Eltern, conn.target = Kind.
  const onReconnect = useCallback((_oldEdge: Edge, conn: Connection) => {
    if (conn.source && conn.target && conn.source !== conn.target) {
      onReparent?.(conn.target, conn.source);
    }
  }, [onReparent]);

  return (
    <Box sx={{ height: 'calc(100vh - 340px)', minHeight: 420, border: '1px solid var(--pm-border)', borderRadius: 1, overflow: 'hidden' }}>
      <ReactFlow
        nodes={nodes}
        edges={layouted.edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onReconnect={onReconnect}
        fitView
        minZoom={0.2}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </Box>
  );
}

/**
 * Interaktives WBS-Diagramm: Arbeitspakete als Knoten in Hierarchie-Ebenen
 * (oben = übergeordnet). Knoten sind verschiebbar; eine Verbindung am Ende greifen
 * und auf ein anderes Eltern-AP ziehen hängt das Paket um (parent_id), die Nummern
 * werden dabei neu vergeben. Zyklen werden verhindert.
 */
export function ApWbsTree({ projectName, items, onReparent }: ApWbsTreeProps) {
  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', color: 'var(--pm-fg-muted)', mb: 1 }}>
        {projectName} — Arbeitspakete verschieben (ziehen); eine Verbindung am Ende greifen und auf ein anderes
        übergeordnetes AP ziehen, um es umzuhängen. Die Nummerierung passt sich automatisch an.
      </Typography>
      <ReactFlowProvider>
        <Canvas items={items} onReparent={onReparent} />
      </ReactFlowProvider>
    </Box>
  );
}
